import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Globe, Instagram, MapPin, MessageCircle } from "lucide-react";
import Logo from "./Logo";
import { BRAND, CONTACT } from "../data/site";

const ease = [0.16, 1, 0.3, 1];
const rise = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.9, ease } }),
};

function VisitingCard() {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rX = useSpring(useTransform(my, [0, 1], [10, -10]), { stiffness: 120, damping: 16 });
  const rY = useSpring(useTransform(mx, [0, 1], [-10, 10]), { stiffness: 120, damping: 16 });

  return (
    <motion.div
      data-testid="visiting-card"
      ref={ref}
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
      }}
      onMouseLeave={() => {
        mx.set(0.5);
        my.set(0.5);
      }}
      style={{ rotateX: rX, rotateY: rY, transformPerspective: 900 }}
      className="relative w-full max-w-md rounded-2xl border border-gold-faint bg-ivory p-7 shadow-[0_40px_80px_-40px_rgba(26,25,23,0.35)]"
    >
      <div className="pointer-events-none absolute inset-2 rounded-xl border border-gold-faint" />
      <svg viewBox="0 0 100 100" className="absolute -left-3 -top-3 h-20 w-20 text-gold/50" fill="none" stroke="currentColor" strokeWidth="1.2">
        <path d="M10 90 Q30 60 20 30 Q40 45 35 10 Q55 35 50 5 Q70 30 65 55 Q80 45 90 70 Q70 75 60 90" />
      </svg>
      <div className="relative flex flex-col items-center gap-1 pt-4 text-center">
        <Logo className="h-14 w-14" />
        <h3 className="font-serif text-3xl font-semibold tracking-[0.08em]">VAYU</h3>
        <p className="text-[11px] uppercase tracking-[0.45em] text-gold-dark">Celebrations</p>
        <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-clay">{BRAND.tagline}</p>
        <div className="my-4 h-px w-24 bg-gold-faint" />
        <p className="font-script text-3xl leading-none text-gold-dark">{BRAND.founder}</p>
        <p className="text-[10px] uppercase tracking-[0.35em] text-clay">{BRAND.role}</p>
        <div className="mt-5 grid w-full grid-cols-1 gap-2 text-left text-xs text-clay">
          <a data-testid="card-whatsapp-link" href={`https://wa.me/${CONTACT.whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 transition-colors hover:text-gold-dark">
            <MessageCircle className="h-3.5 w-3.5 text-gold" /> {CONTACT.whatsappDisplay} · WhatsApp
          </a>
          <a data-testid="card-instagram-link" href={CONTACT.instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 transition-colors hover:text-gold-dark">
            <Instagram className="h-3.5 w-3.5 text-gold" /> {CONTACT.instagram} · Instagram
          </a>
          <span className="flex items-center gap-2.5">
            <MapPin className="h-3.5 w-3.5 text-gold" /> {CONTACT.address}
          </span>
          <span className="flex items-center gap-2.5">
            <Globe className="h-3.5 w-3.5 text-gold" /> {CONTACT.website}
          </span>
        </div>
        <p className="mt-5 font-script text-xl text-gold-dark">{BRAND.motto}</p>
      </div>
    </motion.div>
  );
}

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="grain relative overflow-hidden bg-cream py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="order-2 flex justify-center lg:order-1"
        >
          <motion.div variants={rise} custom={0} className="w-full max-w-md">
            <VisitingCard />
          </motion.div>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} className="order-1 lg:order-2">
          <motion.p variants={rise} custom={0} className="mb-5 text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark">
            About Us
          </motion.p>
          <motion.h2 variants={rise} custom={1} className="font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl">
            Where Vayu began —<br />
            <span className="italic text-gold-dark">one balloon arch</span> at a time.
          </motion.h2>
          <motion.p variants={rise} custom={2} className="mt-6 text-base leading-relaxed text-clay sm:text-lg">
            Vayu Celebrations is a young event-planning startup from Moradabad, founded by{" "}
            {BRAND.founder} — a hands-on planner who pours his heart into every booking like it's a
            family occasion. Birthdays are our first love and speciality, but from a first birthday
            to a golden anniversary, we design décor, cakes, gifts and room styling as one seamless
            story — you bring the date, we bring the magic.
          </motion.p>
          <motion.p variants={rise} custom={3} className="mt-4 text-base leading-relaxed text-clay sm:text-lg">
            We work across the city and nearby towns — homes, farmhouses, banquet halls and open
            lawns — handling everything from concept boards and theme styling to on-day setup, so
            you can simply arrive and celebrate.
          </motion.p>
          <motion.p variants={rise} custom={4} className="mt-8 font-script text-3xl text-gold-dark">
            {BRAND.motto}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}
